import React from 'react'
import Icon from '@mdi/react'
import Tooltip from '@material-ui/core/Tooltip'
import {
  mdiAlphaABox,
  mdiAlphaCBox,
  mdiAlphaSBox,
  mdiAngleAcute,
  mdiAngleRight,
  mdiAnimationOutline,
  mdiArrowSplitHorizontal,
  mdiEyeOff,
  mdiBell,
  mdiCheckboxIntermediate,
  mdiCloudCheck,
  mdiCloudLock,
  mdiContentCut,
  mdiCubeOutline,
  mdiCursorDefault,
  mdiCursorPointer,
  mdiDatabase,
  mdiDownload,
  mdiEllipse,
  mdiExitToApp,
  mdiEyedropper,
  mdiFileCad,
  mdiFileDocument,
  mdiFullscreen,
  mdiFullscreenExit,
  mdiGesture,
  mdiHospitalBox,
  mdiImageOutline,
  mdiInformationOutline,
  mdiInboxArrowDown,
  mdiHistory,
  mdiClipboardListOutline,
  mdiInvertColors,
  mdiCamera,
  mdiShareVariant,
  mdiAutoFix,
  mdiLayers,
  mdiChartHistogram,
  mdiMagnify,
  mdiRectangle,
  mdiRefresh,
  mdiRuler,
  mdiVectorLink,
  mdiVideo,
  mdiViewColumn,
  mdiViewGridPlusOutline,
  mdiWhiteBalanceSunny,
  mdiCrosshairsGps,
  mdiMonitor,
  mdiPlusBox,
} from '@mdi/js'
import WWPresetManager from '../../components/WWPresetManager'
import DsaMenu from '../../components/DsaMenu'
import { RENDER_MODE } from '../../utils/renderMode'
import { PET_CLUT_IDS, PET_CLUT_LABELS } from '../../utils/petClut'
import DoseBadge from '../../components/dose/DoseBadge'
import { portalSectionVisible } from '../../plan/portalChrome'
import { getPwaInstallLabel } from '../../utils/pwaInstallPrompt'
import { t } from '../../i18n'
import packageJson from '../../../package.json'
import GatewayStatusBadge from '../../components/GatewayStatusBadge'
import PortalDisclaimerRail from '../../components/PortalDisclaimerRail'
import VoiceNoteButton from '../../components/VoiceNoteButton'
import { attachBoostedWheelScroll } from '../../utils/panelWheelScroll'

const ICON = 0.82

function ToolBtn({ title, path, active, disabled, onClick }) {
  return (
    <Tooltip title={title} placement="left">
      <span>
        <button
          type="button"
          className={`udv-studio-tool-btn${active ? ' is-active' : ''}`}
          aria-label={title}
          aria-pressed={!!active}
          disabled={!!disabled}
          onClick={onClick}
        >
          <Icon path={path} size={ICON} color="currentColor" />
        </button>
      </span>
    </Tooltip>
  )
}

function Chip({ title, label, active, onClick }) {
  return (
    <Tooltip title={title} placement="left">
      <button
        type="button"
        className={`udv-studio-chip${active ? ' is-active' : ''}`}
        onClick={onClick}
      >
        {label}
      </button>
    </Tooltip>
  )
}

function Section({ label, children }) {
  return (
    <div className="udv-studio-tools-section">
      {label ? (
        <div className="udv-studio-tools-section-label">{label}</div>
      ) : null}
      <div className="udv-studio-tools-grid">{children}</div>
    </div>
  )
}

function Span({ children }) {
  return <div className="udv-studio-tools-span">{children}</div>
}

/**
 * Rail derecho de tools — impronta Studio.
 * Solo dispara handlers del chromeContract; no toca core clínico.
 */
function StudioToolsRail(props) {
  const toolsScrollRef = React.useRef(null)

  React.useEffect(() => {
    return attachBoostedWheelScroll(toolsScrollRef.current)
  }, [])

  const {
    toolActive,
    toolExecute,
    referenceLines,
    showReferenceLines = true,
    hideReferenceLinesOnActive = false,
    seriesLink,
    seriesLinkWwwc,
    anyOpen,
    showCine,
    cineActive,
    showDsa,
    dsaEnabled,
    dsaMaskMode,
    dsaFrameCount,
    dsaPixelShift,
    dsaInvert,
    dsaGain,
    dsaOffset,
    dsaNoiseFloor,
    dsaEngineMode,
    dsaSynthMode,
    dsaRoadmap,
    onDsaSelect,
    showOrthanc,
    onOrthanc,
    showGateway,
    gatewayConnected,
    gatewayStatus,
    onGateway,
    onGatewayOpen,
    gatewayCompatOk,
    showLocalStudies,
    onLocalStudies,
    showRelatedStudies,
    onRelatedStudies,
    showWorklist,
    onWorklist,
    compareMode,
    compareModeEnabled,
    onToggleCompareMode,
    isFullscreen,
    showPresets,
    currentModality,
    onPresetSelect,
    onLayout,
    onLayoutToggle,
    layoutPinned,
    onFullscreen,
    onPwaExit,
    onInstallPwa,
    pwaStandalone,
    swUpdatePending,
    onOpenSwUpdate,
    onReset,
    onExplorer,
    onCine,
    renderMode,
    onToggleRenderMode,
    studyDownloadUrl,
    localStudyZipEligible,
    localStudyZipReady,
    studyZipDownloadTitle,
    studyZipDownloadBusy,
    onDownloadStudy,
    mprAllowed,
    mprOrthogonalAllowed,
    mprActive,
    mprMessage,
    visibleMprOrthogonal,
    visibleMprCoronal,
    visibleMprSagittal,
    visibleMprAxial,
    onMpr,
    onMprExit,
    mprCrosshairActive,
    onMprCrosshairToggle,
    mprSlabMode,
    mprSlabMm,
    onMprSlabMode,
    onMprSlabMm,
    petFusionOn,
    petFusionOpacity,
    petFusionClut,
    petFusionWindowMax,
    petFusionAvailable,
    onPetFusionToggle,
    onPetFusionOpacity,
    onPetFusionClut,
    onPetFusionWindowMax,
    gspsAvailable,
    gspsOn,
    onGspsToggle,
    onGspsExport,
    onSrExport,
    onAttachPdf,
    mpr3dAllowed,
    visibleMpr3D,
    mpr3dDecapado,
    mpr3dMessage,
    onMpr3d,
    onMpr3dDecapado,
    scoutAvailable,
    scoutSyncEnabled,
    scoutPanelOpen,
    onScoutSyncToggle,
    onScoutPanelToggle,
    doseReport,
    onDoseInfo,
    headerOpen,
    onHeader,
    histogramOpen,
    onHistogram,
    onGsdfQa,
    gsdfQaPassed,
    onCaptureViewport,
    onShareViewport,
    onCopilotOpen,
    voiceNoteRecording,
    voiceNoteBusy,
    voiceNoteHoldToTalk,
    onVoiceNoteStart,
    onVoiceNoteStop,
    onVoiceNoteToggle,
    onVoiceNoteOpenPanel,
    portalChrome = null,
    showPortalDisclaimerRail = false,
    className = '',
  } = props

  const run = (tool) => {
    if (toolExecute) toolExecute(tool)
  }

  const isPreview = renderMode === RENDER_MODE.PREVIEW
  const showSec = (id) => portalSectionVisible(portalChrome, id)

  const rootClass = ['udv-studio-tools', className].filter(Boolean).join(' ')

  return (
    <div
      ref={toolsScrollRef}
      className={rootClass}
      role="toolbar"
      aria-label="Herramientas Studio"
    >
      {showSec('sistema') ? (
      <Section label="Sistema">
        <ToolBtn
          title={
            layoutPinned
              ? 'Layout (ocultar acceso rápido)'
              : 'Layout (mostrar acceso rápido)'
          }
          path={mdiViewGridPlusOutline}
          active={!!layoutPinned}
          onClick={(e) => {
            if (onLayoutToggle) onLayoutToggle(e)
            else if (onLayout) onLayout(e)
          }}
        />
        <ToolBtn
          title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
          path={isFullscreen ? mdiFullscreenExit : mdiFullscreen}
          active={!!isFullscreen}
          onClick={() => onFullscreen && onFullscreen()}
        />
        {!pwaStandalone && onInstallPwa ? (
          <ToolBtn
            title={getPwaInstallLabel(t)}
            path={mdiPlusBox}
            onClick={() => onInstallPwa()}
          />
        ) : null}
        <ToolBtn
          title={pwaStandalone ? 'Salir de UDV' : 'Salir de UDV (solo en app instalada)'}
          path={mdiExitToApp}
          disabled={!pwaStandalone}
          onClick={() => onPwaExit && onPwaExit()}
        />
        <ToolBtn
          title={
            swUpdatePending
              ? 'Avisos / notificaciones'
              : 'Sin avisos pendientes'
          }
          path={mdiBell}
          active={!!swUpdatePending}
          disabled={!swUpdatePending}
          onClick={() => onOpenSwUpdate && onOpenSwUpdate()}
        />
        <ToolBtn
          title={anyOpen ? 'Reset Image' : 'Reset (sin imagen)'}
          path={mdiRefresh}
          disabled={!anyOpen}
          onClick={(e) => {
            if (e && e.preventDefault) e.preventDefault()
            if (onReset) onReset()
          }}
        />
        <ToolBtn
          title="Series (mostrar/ocultar)"
          path={mdiAnimationOutline}
          onClick={() => onExplorer && onExplorer()}
        />
        {showOrthanc ? (
          <ToolBtn
            title="Connect to Orthanc"
            path={mdiDatabase}
            onClick={() => onOrthanc && onOrthanc()}
          />
        ) : null}
        <ToolBtn
          title={
            !showGateway
              ? 'Inicio de sesión profesional (solo con invite de clínica)'
              : gatewayConnected
                ? 'TJS Gateway (conectado)'
                : 'TJS Gateway / Iniciar sesión'
          }
          path={gatewayConnected ? mdiCloudCheck : mdiCloudLock}
          active={!!gatewayConnected}
          disabled={!showGateway}
          onClick={() => onGateway && onGateway()}
        />
        {showLocalStudies ? (
          <ToolBtn
            title="Estudios en storage local"
            path={mdiInboxArrowDown}
            onClick={() => onLocalStudies && onLocalStudies()}
          />
        ) : null}
        {(studyDownloadUrl || localStudyZipEligible) && (
          <ToolBtn
            title={
              studyZipDownloadTitle ||
              (studyDownloadUrl || localStudyZipReady
                ? 'Descargar estudio (.zip)'
                : 'Descarga no disponible')
            }
            path={mdiDownload}
            disabled={
              !!studyZipDownloadBusy ||
              !(studyDownloadUrl || localStudyZipReady)
            }
            onClick={() => onDownloadStudy && onDownloadStudy()}
          />
        )}
      </Section>
      ) : null}

      {showSec('nav') ? (
      <Section label="Nav">
        <ToolBtn
          title="No Tool"
          path={mdiCursorDefault}
          active={toolActive === 'notool' || !toolActive}
          onClick={() => run('notool')}
        />
        {showReferenceLines ? (
          <ToolBtn
            title="Reference Lines"
            path={mdiArrowSplitHorizontal}
            active={!!referenceLines}
            disabled={!anyOpen}
            onClick={() => run('referencelines')}
          />
        ) : null}
        {showReferenceLines && referenceLines ? (
          <ToolBtn
            title="Ocultar línea en viewport activo"
            path={mdiEyeOff}
            active={!!hideReferenceLinesOnActive}
            disabled={!anyOpen}
            onClick={() => run('referencelinesactivehide')}
          />
        ) : null}
        <ToolBtn
          title="Link Series"
          path={mdiVectorLink}
          active={!!seriesLink}
          disabled={!!mprActive}
          onClick={() => run('serieslink')}
        />
        {seriesLink || mprActive ? (
          <ToolBtn
            title={mprActive ? 'Link WW/WL (planos MPR)' : 'Link WW/WL'}
            path={mdiWhiteBalanceSunny}
            active={!!seriesLinkWwwc}
            onClick={() => run('serieslinkwwwc')}
          />
        ) : null}
      </Section>
      ) : null}

      {anyOpen && showSec('pet') ? (
        <Section label="PET">
          <ToolBtn
            title={
              petFusionAvailable
                ? 'Superponer PET sobre CT/MR'
                : 'No hay serie PET en el estudio'
            }
            path={mdiLayers}
            active={!!petFusionOn}
            disabled={!petFusionAvailable && !petFusionOn}
            onClick={() => onPetFusionToggle && onPetFusionToggle()}
          />
          {petFusionOn ? (
            <div className="udv-studio-chip-row">
              {PET_CLUT_IDS.map((id) => (
                <Chip
                  key={id}
                  title={PET_CLUT_LABELS[id]}
                  label={id === 'hotMetal' ? 'HM' : id === 'rainbow' ? 'RB' : 'Fire'}
                  active={(petFusionClut || 'hotMetal') === id}
                  onClick={() => onPetFusionClut && onPetFusionClut(id)}
                />
              ))}
              {[25, 50, 75, 100].map((pct) => (
                <Chip
                  key={`op-${pct}`}
                  title={`Opacidad ${pct}%`}
                  label={`${pct}`}
                  active={Number(petFusionOpacity) === pct}
                  onClick={() => onPetFusionOpacity && onPetFusionOpacity(pct)}
                />
              ))}
              {[5, 10, 15].map((mx) => (
                <Chip
                  key={`suv-${mx}`}
                  title={`Ventana SUV 0–${mx}`}
                  label={`SUV${mx}`}
                  active={Number(petFusionWindowMax) === mx}
                  onClick={() => onPetFusionWindowMax && onPetFusionWindowMax(mx)}
                />
              ))}
            </div>
          ) : null}
        </Section>
      ) : null}

      {anyOpen && showSec('gsps') ? (
        <Section label="GSPS">
          <ToolBtn
            title={
              gspsAvailable
                ? gspsOn
                  ? 'Ocultar GSPS importado'
                  : 'Aplicar GSPS'
                : 'No hay serie PR (GSPS)'
            }
            path={mdiImageOutline}
            active={!!gspsOn && !!gspsAvailable}
            disabled={!gspsAvailable && !gspsOn}
            onClick={() => onGspsToggle && onGspsToggle()}
          />
          <ToolBtn
            title="Exportar mediciones a GSPS (.dcm)"
            path={mdiFileCad}
            onClick={() => onGspsExport && onGspsExport()}
          />
          <ToolBtn
            title="Exportar informe SR (.dcm)"
            path={mdiFileDocument}
            onClick={() => onSrExport && onSrExport()}
          />
          <ToolBtn
            title="Adjuntar PDF / informe (DOC)"
            path={mdiFileCad}
            onClick={() => onAttachPdf && onAttachPdf()}
          />
        </Section>
      ) : null}

      {anyOpen && onMpr && showSec('mpr') ? (
        <Section label="MPR">
          {mprActive ? (
            <ToolBtn
              title="Salir de MPR"
              path={mdiRefresh}
              onClick={() => onMprExit && onMprExit()}
            />
          ) : null}
          {mprOrthogonalAllowed ? (
            <ToolBtn
              title={mprAllowed ? 'MPR Orthogonal' : mprMessage || 'MPR no disponible'}
              path={mdiViewColumn}
              active={!!visibleMprOrthogonal}
              disabled={!mprAllowed}
              onClick={() => onMpr('orthogonal')}
            />
          ) : null}
          <ToolBtn
            title={mprAllowed ? 'MPR Coronal' : mprMessage || 'MPR no disponible'}
            path={mdiAlphaCBox}
            active={!!visibleMprCoronal}
            disabled={!mprAllowed}
            onClick={() => onMpr('coronal')}
          />
          <ToolBtn
            title={mprAllowed ? 'MPR Sagittal' : mprMessage || 'MPR no disponible'}
            path={mdiAlphaSBox}
            active={!!visibleMprSagittal}
            disabled={!mprAllowed}
            onClick={() => onMpr('sagittal')}
          />
          <ToolBtn
            title={mprAllowed ? 'MPR Axial' : mprMessage || 'MPR no disponible'}
            path={mdiAlphaABox}
            active={!!visibleMprAxial}
            disabled={!mprAllowed}
            onClick={() => onMpr('axial')}
          />
          {mprActive ? (
            <ToolBtn
              title="Punto mira"
              path={mdiCrosshairsGps}
              active={!!mprCrosshairActive}
              onClick={() => onMprCrosshairToggle && onMprCrosshairToggle()}
            />
          ) : null}
          {mprActive && onMprSlabMode ? (
            <div className="udv-studio-chip-row">
              <Chip
                title="Corte fino"
                label="Fino"
                active={(mprSlabMode || 'thin') === 'thin'}
                onClick={() => onMprSlabMode && onMprSlabMode('thin')}
              />
              <Chip
                title="MIP"
                label="MIP"
                active={mprSlabMode === 'mip'}
                onClick={() => onMprSlabMode && onMprSlabMode('mip')}
              />
              <Chip
                title="MinIP"
                label="MinIP"
                active={mprSlabMode === 'minip'}
                onClick={() => onMprSlabMode && onMprSlabMode('minip')}
              />
              <Chip
                title="Promedio"
                label="Avg"
                active={mprSlabMode === 'avg'}
                onClick={() => onMprSlabMode && onMprSlabMode('avg')}
              />
              {[0, 3, 5, 10].map((mm) => (
                <Chip
                  key={`slab-${mm}`}
                  title={mm === 0 ? 'Sin grosor' : `Grosor ${mm} mm`}
                  label={mm === 0 ? 'Off' : `${mm}`}
                  active={Number(mprSlabMm) === mm}
                  onClick={() => onMprSlabMm && onMprSlabMm(mm)}
                />
              ))}
            </div>
          ) : null}
          {mprActive ? (
            <ToolBtn
              title={
                mpr3dAllowed
                  ? visibleMpr3D
                    ? 'Volver a MPR'
                    : '3D'
                  : mpr3dMessage || '3D no disponible'
              }
              path={mdiCubeOutline}
              active={!!visibleMpr3D}
              disabled={!mpr3dAllowed}
              onClick={() => onMpr3d && onMpr3d()}
            />
          ) : null}
          {visibleMpr3D ? (
            <ToolBtn
              title={
                mpr3dDecapado
                  ? 'Volver al volumen completo'
                  : 'Decapar según planos MPR'
              }
              path={mdiContentCut}
              active={!!mpr3dDecapado}
              onClick={() => onMpr3dDecapado && onMpr3dDecapado()}
            />
          ) : null}
          {mprActive && scoutAvailable ? (
            <ToolBtn
              title="Scout Sync"
              path={mdiLayers}
              active={!!scoutSyncEnabled}
              onClick={() => onScoutSyncToggle && onScoutSyncToggle()}
            />
          ) : null}
          {mprActive && scoutAvailable ? (
            <ToolBtn
              title="Panel Scout"
              path={mdiImageOutline}
              active={!!scoutPanelOpen}
              onClick={() => onScoutPanelToggle && onScoutPanelToggle()}
            />
          ) : null}
        </Section>
      ) : null}

      {showSec('imagen') ? (
      <Section label="Imagen">
        <ToolBtn
          title={showCine ? 'Cine Player' : 'Cine (no disponible)'}
          path={mdiVideo}
          active={!!cineActive}
          disabled={!showCine}
          onClick={() => onCine && onCine()}
        />
        <ToolBtn
          title="WW/WC"
          path={mdiWhiteBalanceSunny}
          active={toolActive === 'Wwwc'}
          onClick={() => run('Wwwc')}
        />
        {showPresets && onPresetSelect ? (
          <span>
            <WWPresetManager
              skin="studio"
              placement="right"
              currentModality={currentModality}
              onPresetSelect={onPresetSelect}
            />
          </span>
        ) : null}
        <ToolBtn
          title="Invertir"
          path={mdiInvertColors}
          disabled={!anyOpen}
          onClick={() => run('Invert')}
        />
        {showDsa && onDsaSelect ? (
          <span>
            <DsaMenu
              enabled={!!dsaEnabled}
              maskMode={dsaMaskMode}
              frameCount={dsaFrameCount}
              pixelShift={dsaPixelShift}
              invertDSA={!!dsaInvert}
              gain={dsaGain}
              offset={dsaOffset}
              noiseFloor={dsaNoiseFloor}
              engineMode={dsaEngineMode || 'auto'}
              synthMode={dsaSynthMode || 'minip'}
              roadmap={dsaRoadmap}
              onSelect={onDsaSelect}
              triggerClassName={`udv-studio-tool-btn${dsaEnabled ? ' is-active' : ''}`}
              triggerIconSize={ICON}
              triggerStyle={{
                width: '100%',
                height: 'var(--udv-btn-size)',
                minHeight: 'var(--udv-btn-size)',
                minWidth: 0,
                padding: 0,
                borderRadius: 4,
                color: 'inherit',
              }}
              menuProps={{
                style: { zIndex: 160000 },
                getContentAnchorEl: null,
                anchorOrigin: { vertical: 'center', horizontal: 'left' },
                transformOrigin: { vertical: 'center', horizontal: 'right' },
              }}
            />
          </span>
        ) : null}
        <ToolBtn
          title="Histograma"
          path={mdiChartHistogram}
          active={!!histogramOpen}
          disabled={!anyOpen}
          onClick={() => onHistogram && onHistogram()}
        />
        <ToolBtn
          title="Capturar viewport (JPG/PNG)"
          path={mdiCamera}
          disabled={!anyOpen}
          onClick={() => (onCaptureViewport ? onCaptureViewport() : run('saveas'))}
        />
        <ToolBtn
          title="Compartir"
          path={mdiShareVariant}
          disabled={!anyOpen}
          onClick={() => (onShareViewport ? onShareViewport() : run('share'))}
        />
        {onCopilotOpen ? (
          <ToolBtn
            title="UDV AI Copilot"
            path={mdiAutoFix}
            disabled={!anyOpen}
            onClick={() => onCopilotOpen()}
          />
        ) : null}
        {onVoiceNoteToggle || onVoiceNoteOpenPanel ? (
        <span style={{ display: 'inline-flex', justifyContent: 'center' }}>
          <VoiceNoteButton
            holdToTalk={!!voiceNoteHoldToTalk}
            recording={!!voiceNoteRecording}
            busy={!!voiceNoteBusy}
            disabled={!anyOpen}
            iconSize={ICON}
            color="currentColor"
            onStart={onVoiceNoteStart}
            onStop={onVoiceNoteStop}
            onToggle={onVoiceNoteToggle}
            onLongPress={onVoiceNoteOpenPanel}
            showSubmenuHint
            className={`udv-studio-tool-btn${voiceNoteRecording ? ' is-active' : ''}`}
            style={{
              width: '100%',
              height: 'var(--udv-btn-size)',
              minHeight: 'var(--udv-btn-size)',
              minWidth: 0,
              padding: 0,
              borderRadius: 4,
              position: 'relative',
              touchAction: 'none',
            }}
          />
        </span>
        ) : null}
        <ToolBtn
          title="Pan"
          path={mdiCursorPointer}
          active={toolActive === 'Pan'}
          onClick={() => run('Pan')}
        />
        <ToolBtn
          title="Zoom"
          path={mdiMagnify}
          active={toolActive === 'Zoom'}
          onClick={() => run('Zoom')}
        />
      </Section>
      ) : null}

      {showSec('estudio') ? (
      <Section label="Estudio">
        <ToolBtn
          title="Metadatos DICOM (cabecera)"
          path={mdiFileDocument}
          active={!!headerOpen}
          disabled={!anyOpen}
          onClick={() => onHeader && onHeader()}
        />
        {showRelatedStudies ? (
          <ToolBtn
            title={t('relatedStudies.menuTooltip')}
            path={mdiHistory}
            onClick={() => onRelatedStudies && onRelatedStudies()}
          />
        ) : null}
        {showWorklist ? (
          <ToolBtn
            title={t('worklist.menuTooltip')}
            path={mdiClipboardListOutline}
            onClick={() => onWorklist && onWorklist()}
          />
        ) : null}
        <ToolBtn
          title={
            compareMode
              ? t('relatedStudies.compareModeOff')
              : compareModeEnabled
                ? t('relatedStudies.compareModeOn')
                : t('relatedStudies.compareModeDisabled')
          }
          path={mdiViewColumn}
          active={!!compareMode}
          disabled={!compareMode && !compareModeEnabled}
          onClick={() => onToggleCompareMode && onToggleCompareMode()}
        />
        {doseReport ? (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 0,
            }}
          >
            <DoseBadge report={doseReport} onClick={() => onDoseInfo && onDoseInfo()} />
          </span>
        ) : (
          <ToolBtn
            title="Info de dosis (radiación del estudio)"
            path={mdiInformationOutline}
            disabled={!anyOpen}
            onClick={() => onDoseInfo && onDoseInfo()}
          />
        )}
      </Section>
      ) : null}

      {showSec('medir') ? (
      <Section label="Medir">
        <ToolBtn
          title="Lupa"
          path={mdiCheckboxIntermediate}
          active={toolActive === 'Magnify'}
          onClick={() => run('Magnify')}
        />
        <ToolBtn
          title="Regla"
          path={mdiRuler}
          active={toolActive === 'Length'}
          onClick={() => run('Length')}
        />
        <ToolBtn
          title="Sonda"
          path={mdiEyedropper}
          active={toolActive === 'Probe'}
          onClick={() => run('Probe')}
        />
        <ToolBtn
          title="Ángulo"
          path={mdiAngleAcute}
          active={toolActive === 'Angle'}
          onClick={() => run('Angle')}
        />
        <ToolBtn
          title="Cobb"
          path={mdiAngleRight}
          active={toolActive === 'CobbAngle'}
          onClick={() => run('CobbAngle')}
        />
      </Section>
      ) : null}

      {showSec('roi') ? (
      <Section label="ROI">
        <ToolBtn
          title="Elliptical Roi"
          path={mdiEllipse}
          active={toolActive === 'EllipticalRoi'}
          onClick={() => run('EllipticalRoi')}
        />
        <ToolBtn
          title="Rectangle Roi"
          path={mdiRectangle}
          active={toolActive === 'RectangleRoi'}
          onClick={() => run('RectangleRoi')}
        />
        <ToolBtn
          title="Freehand Roi"
          path={mdiGesture}
          active={toolActive === 'FreehandRoi'}
          onClick={() => run('FreehandRoi')}
        />
      </Section>
      ) : null}

      {onToggleRenderMode && showSec('render') ? (
        <Section label="Render">
          <ToolBtn
            title={isPreview ? 'Modo Preview (tocar → Diagnóstico)' : 'Modo Diagnóstico'}
            path={isPreview ? mdiImageOutline : mdiHospitalBox}
            active={!isPreview}
            onClick={() => onToggleRenderMode()}
          />
        </Section>
      ) : null}

      {onGsdfQa && showSec('qa') ? (
        <Section label="QA">
          <ToolBtn
            title="QA de pantalla (GSDF / TG18-QC)"
            path={mdiMonitor}
            active={!!gsdfQaPassed}
            onClick={() => onGsdfQa()}
          />
        </Section>
      ) : null}

      <div className="udv-studio-tools-footer">
        {showPortalDisclaimerRail ? <PortalDisclaimerRail /> : null}
        {showSec('gatewayBadge') ? (
        <GatewayStatusBadge
          className="udv-studio-gateway-badge"
          status={gatewayStatus || 'local_only'}
          compatOk={gatewayCompatOk !== false}
          onClick={() => {
            if (typeof onGatewayOpen === 'function') onGatewayOpen()
            else if (typeof onGateway === 'function') onGateway()
          }}
        />
        ) : null}
        {onGsdfQa && showSec('qa') ? (
          <button
            type="button"
            className={`udv-studio-qa-badge${gsdfQaPassed ? ' is-ok' : ' is-unverified'}`}
            onClick={() => onGsdfQa()}
            aria-label={gsdfQaPassed ? 'QA Screen: OK' : 'QA Screen: Unverified'}
          >
            {gsdfQaPassed ? 'QA Screen: OK' : 'QA Screen: Unverified'}
          </button>
        ) : null}
        <div>Studio {packageJson.version}</div>
      </div>
    </div>
  )
}

export default StudioToolsRail
